import axiosInstance from "../api/axiosInstance";
import type { TNotes } from "../types/index.type";

type TWriteNoteBody = {
  audio_id?: number;
  note_text?: string;
  title: string;
  note_type: string | number | undefined;
  category_type: string | number;
  modality_mode?: string;
  gender_type: string | number;
  language_type?: number;
  location_type?: number;
  duration_type?: number | null;
  client_id?: number;
  template_id?: string | number | undefined;
  customization?: {
    customization_type: 0 | 1 | 2;
    note_type_instructions: {
      [key: number]:
        | string
        | {
            note: string;
            is_system: boolean;
          };
    };
    is_system: boolean;
    note_section: string;
    detail_level: number;
    formatting: number;
  };
  is_capture?: boolean;
  system_metadata?: { [key: string]: any };
  duration_addon_type?: number;
  is_locked?: boolean;
};

type TCancelRecordingBody = {
  audio_id: string;
  status: string;
};

export const handleDelete = (recordingId: number) => {
  return axiosInstance.delete(`/notes/delete/${recordingId}`);
};

export const getNoteAudioPlaybackURL = (noteId: number) => {
  /* Returns a short-lived GCS presigned URL for streaming the note's audio recording. */
  return axiosInstance.get(`/notes/audio_url/${noteId}/`);
};

export const handleCaptureAudioSubmission = (formData: FormData) => {
  /*  This is to finalize a recording sent via web sockets to Render
   */
  return axiosInstance.post("/notes/capture_audio/", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const handleUploadAudioSubmission = (formData: FormData) => {
  /* This is to upload a new audio file (any format) to the server which also creates a new entry in the database. The upload modality case.
   */
  return axiosInstance.post("/notes/upload_audio/", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const handleUploadAudioFromIndexedDBSubmission = (formData: FormData) => {
  /* This is to upload the actual audio file (webm) to GCS as a backup for the audio file sent via web sockets to Render
   */
  return axiosInstance.post("/notes/upload_audio_gcs/", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

/**
 * Get a signed URL for direct-to-GCS upload of IndexedDB audio backup.
 */
export const getIndexedDBUploadSignedURL = (audioId: string, contentType = "audio/webm") => {
  return axiosInstance.post<{
    signed_url: string;
    content_type: string;
  }>("/notes/get_indexeddb_upload_url/", {
    audio_id: audioId,
    content_type: contentType,
  });
};

/**
 * Upload audio from IndexedDB directly to GCS using a signed URL.
 * Fails silently since this is a backup mechanism.
 */
export const uploadAudioFromIndexedDBViaSignedURL = async (
  audioId: string,
  audioBlob: Blob,
  contentType = "audio/webm",
): Promise<boolean> => {
  try {
    const response = await getIndexedDBUploadSignedURL(audioId, contentType);
    const { signed_url } = response.data;

    const uploadResponse = await fetch(signed_url, {
      method: "PUT",
      headers: { "Content-Type": contentType },
      body: audioBlob,
    });

    if (uploadResponse.ok) {
      console.log(`IndexedDB backup uploaded successfully for audio_id=${audioId}`);
      return true;
    }
    console.warn(`IndexedDB backup upload failed with status ${uploadResponse.status}`);
    return false;
  } catch (error) {
    console.warn("IndexedDB backup upload failed:", error);
    return false;
  }
};

export const handleUploadAudioSubmissionForTranscription = (formData: FormData) => {
  /* This is to upload the actual audio file (webm) to the server for transcription when there is already an audiofile row in the database.
   */
  return axiosInstance.post("/notes/upload_audio_mobile/", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const handleWriteNoteSubmission = (body: TWriteNoteBody) => {
  return axiosInstance.post(`/notes/write_note/`, body);
};

export const handleWriteNoteSubmissionWithContext = (body: TWriteNoteBody, files: File[]) => {
  const formData = new FormData();
  for (const [key, value] of Object.entries(body)) {
    if (value === undefined || value === null) continue;
    if (typeof value === "object") {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, String(value));
    }
  }
  files.forEach((file) => formData.append("files", file));
  return axiosInstance.post(`/notes/write_note/`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const handleSubmitNoteForProcessing = (body: Partial<TWriteNoteBody>) => {
  return axiosInstance.post("/notes/process_recording/", body);
};

export const handleCancelRecording = (body: TCancelRecordingBody) => {
  return axiosInstance.put("/notes/update/", body);
};

export const updateNote = (body: Partial<TNotes>) => {
  return axiosInstance.put("/notes/update/", body);
};

export const getProceduralCodes = async (audioFileId: string) => {
  const { data } = await axiosInstance.get(`/coding/procedural-codes/${audioFileId}/`);
  return data;
};

export const generateProceduralCodes = (audioFileId: string) => {
  return axiosInstance.post(`/coding/procedural-codes/${audioFileId}/`);
};

export const regenerateCodes = (audioFileId: number) => {
  return axiosInstance.post(`/coding/regenerate-codes/${audioFileId}/`);
};

export const updateProceduralCode = (
  proceduralCodeId: number,
  data: { psychotherapy_duration_minutes?: number; rationale?: string },
) => {
  return axiosInstance.patch(`/coding/procedural-code/${proceduralCodeId}/`, data);
};

export const generateSessionInsight = (recordingId: number | undefined) => {
  return axiosInstance.post(`/notes/generate/session_insights/`, {
    audio_id: recordingId,
  });
};

export const handleGenerateTreatmentPlan = (recordingId: number | undefined) => {
  return axiosInstance.post(`/notes/generate/treatment_plan/`, {
    audio_id: recordingId,
  });
};

export const handleAVSEmailGenerate = (recordingId: number | undefined) => {
  return axiosInstance.post(`/notes/gen_avs_email/`, { audio_id: recordingId });
};

export const handleGenerateTranscriptOfNote = (recordingId: number | undefined) => {
  return axiosInstance.post(`/notes/gen_transcript/`, {
    audio_id: recordingId,
  });
};

export const completeTutorial = () => {
  return axiosInstance.post(`/users/complete_tutorial/`, {});
};

export const getNotesDetails = (recordingId: number | string | undefined) => {
  return axiosInstance.get(`/notes/fetch/${recordingId}/`);
};

export const getNotesList = (pageNumber?: number, signal?: AbortSignal, filterMode?: string) => {
  const params = new URLSearchParams({
    page: String(pageNumber || 1),
  });

  if (filterMode) {
    params.append("filter", filterMode);
  }

  return axiosInstance.get(`/notes/list/?${params.toString()}`, { signal });
};

export const getNotesListByClient = (clientId: number, pageNumber?: number) => {
  if (pageNumber) {
    return axiosInstance.get(`/notes/client/${clientId}/?page=${pageNumber}`);
  }
  return axiosInstance.get(`/notes/client/${clientId}/`);
};

export const getNotesListByClientUUID = (clientUuid: string, pageNumber?: number, signal?: AbortSignal) => {
  return axiosInstance.get(`/notes/client/uuid/${clientUuid}/?page=${pageNumber || 1}`, { signal });
};

export const getCustomizationList = () => {
  return axiosInstance.get(`notes/customization/list/`);
};

export const saveTemplateInstructions = (noteType: number, instructions: string) => {
  return axiosInstance.post(`notes/save_template_instructions/`, {
    note_type: noteType,
    instructions,
    is_system: true,
  });
};

export const getCustomNote = (noteId: number) => {
  return axiosInstance.get(`/notes/custom_notes/${noteId}/`);
};

export const createCustomNote = (body: {
  name: string;
  document: string;
  prompt?: string;
  coding_instructions?: string;
  from_template?: number;
  settings: { is_addon: boolean; is_cpt_codes: boolean };
  is_group_accessible?: boolean;
}) => {
  return axiosInstance.post(`/notes/custom_notes/`, body);
};

export const updateCustomNote = (
  noteId: number,
  body: {
    name: string;
    document: string;
    prompt?: string;
    coding_instructions?: string;
    settings: { is_addon: boolean; is_cpt_codes: boolean };
    is_group_accessible?: boolean;
  },
) => {
  return axiosInstance.put(`/notes/custom_notes/${noteId}/`, body);
};

export const deleteCustomNote = (noteId: number) => {
  return axiosInstance.delete(`/notes/custom_notes/${noteId}/`);
};

export const getCustomNoteList = () => {
  return axiosInstance.get(`/notes/custom_notes/available_note_types/`);
};

/**
 * Initiate file upload for the upload modality.
 * Creates an AudioFile record and returns a signed URL for direct-to-GCS upload.
 */
export const initiateFileUpload = (data: {
  title: string;
  note_type?: number;
  template_id?: string | number;
  location_type?: number | null;
  duration_type?: number | null;
  duration_addon_type?: number | null;
  client_id?: number | null;
  content_type: string;
  is_locked?: boolean;
  system_metadata?: Record<string, unknown>;
  file_name?: string;
}) => {
  return axiosInstance.post<{
    audio_id: number;
    signed_url: string;
    cloud_path: string;
    content_type: string;
  }>("/notes/initiate-file-upload/", data);
};

/**
 * Upload file directly to GCS using a signed URL.
 */
export const uploadFileToGCS = async (signedUrl: string, file: File, contentType: string): Promise<boolean> => {
  try {
    const response = await fetch(signedUrl, {
      method: "PUT",
      headers: { "Content-Type": contentType },
      body: file,
    });

    if (response.ok) {
      console.log("File uploaded successfully to GCS");
      return true;
    }
    console.error(`GCS upload failed with status ${response.status}`);
    return false;
  } catch (error) {
    console.error("GCS upload failed:", error);
    return false;
  }
};

/**
 * Complete file upload for the upload modality.
 * Called after the file has been uploaded to GCS via signed URL.
 * Triggers transcription processing.
 */
export const completeFileUpload = (audioId: number) => {
  return axiosInstance.post<TNotes>("/notes/complete-file-upload/", {
    audio_id: audioId,
  });
};

export const uploadPDFsAndCreateNote = (files: File[], noteData: any) => {
  const formData = new FormData();

  // Add all PDF files
  files.forEach((file) => {
    formData.append("files", file);
  });

  // Add note metadata (same as audio upload)
  if (noteData.title) formData.append("title", noteData.title);
  if (!Number.isNaN(noteData.note_type)) formData.append("note_type", noteData.note_type.toString());
  if (noteData.template_id) formData.append("template_id", noteData.template_id.toString());
  if (noteData.category_type) formData.append("category_type", noteData.category_type.toString());
  if (noteData.gender_type) formData.append("gender_type", noteData.gender_type.toString());
  if (noteData.language_type) formData.append("language_type", noteData.language_type.toString());
  if (noteData.location_type !== undefined && noteData.location_type !== null)
    formData.append("location_type", noteData.location_type.toString());
  if (noteData.duration_type !== undefined && noteData.duration_type !== null)
    formData.append("duration_type", noteData.duration_type.toString());
  if (noteData.duration_addon_type !== undefined && noteData.duration_addon_type !== null)
    formData.append("duration_addon_type", noteData.duration_addon_type.toString());
  if (noteData.client_id) formData.append("client_id", noteData.client_id.toString());
  if (noteData.is_locked) formData.append("is_locked", "true");
  if (noteData.system_metadata) formData.append("system_metadata", JSON.stringify(noteData.system_metadata));

  return axiosInstance.post(`/notes/upload_pdfs/`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const uploadSessionContextPdfs = (audioId: number, files: File[]) => {
  const formData = new FormData();
  formData.append("audio_id", audioId.toString());
  files.forEach((file) => {
    formData.append("files", file);
  });
  return axiosInstance.post("/notes/upload_session_context_pdfs/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const startEHRTransfer = (audioId: number) => {
  return axiosInstance.post(`/rpa/agent/transfer/`, { audio_id: audioId });
};

export const getEHRTransferStatus = (audioId: number) => {
  return axiosInstance.get(`/rpa/agent/transfer/status/${audioId}/`);
};

export const getActiveTransfer = () => {
  return axiosInstance.get(`/rpa/agent/transfer/active/`);
};

export const selectTransferAppointment = (jobId: string, appointmentId: string) => {
  return axiosInstance.post(`/rpa/agent/transfer/${jobId}/select-appointment/`, {
    appointment_id: appointmentId,
  });
};

export const submitTransferMFACode = (jobId: string, mfaCode?: string, mfaMethod?: string) => {
  return axiosInstance.post(`/rpa/agent/transfer/${jobId}/submit-mfa/`, {
    ...(mfaCode && { mfa_code: mfaCode }),
    ...(mfaMethod && { mfa_method: mfaMethod }),
  });
};

export const cancelEHRTransfer = (jobId: string) => {
  return axiosInstance.post(`/rpa/agent/transfer/${jobId}/cancel/`);
};
